var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// elements/components/x-button.ts
import { html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { fromEvent, takeUntil } from "rxjs";
import { ChemistryLitElement } from "../../chemistry-lit-element.js";
import { css } from "../../directives/css.js";
import { injectAttributesOf } from "../../directives/inject-attributes.js";
import "../theming/coloring-system.js";
import { setGlobalStyle } from "../theming/global-style.js";
import { inputToken } from "../theming/input-token.js";
import { xComponents } from "../x-components.js";
import "./x-flex.js";
import "./x-icon.js";
import "./x-link.js";
setGlobalStyle(() => ({
  "@keyframes rotate": { from: { transform: "rotate(0)" }, to: { transform: "rotate(360deg)" } }
}));
var XButton = class extends ChemistryLitElement {
  constructor() {
    super(...arguments);
    this.icon = "";
    this.mode = "action";
    this.tone = "";
    this.active = false;
    this.loading = false;
    this.tabindex = "0";
    this.visualStyles = () => ({
      ":host": {
        borderRadius: this.sizes.borderRadius,
        "#innerwrapper": { padding: `${this.spacing.sm} ${this.spacing.md}`, flex: "1", position: "relative" },
        "&[disabled]": { opacity: ".5", pointerEvents: "none" },
        "&[loading]": { pointerEvents: "none", filter: "grayscale(.5)", opacity: 0.5 },
        transition: "opacity .12s ease",
        "&:hover": { opacity: 0.9 },
        "&:active": { opacity: 0.7 }
      }
    });
    this.stylesModeActionAndOutline = () => ({
      borderWidth: "1px",
      borderStyle: "solid",
      "#innerwrapper": {
        height: `calc(${this.baseFontSize}px + ${inputToken(this).paddingTop} + ${inputToken(this).paddingBottom})`
      }
    });
    this.stylesModeAction = () => ({
      ":host": {
        ...this.stylesModeActionAndOutline(),
        background: this.tone ? this.colors.tone[this.tone] : this.ctaColor,
        borderColor: this.tone ? this.colors.tone[this.tone] : this.ctaColor,
        color: this.tone ? this.colors.on.tone[this.tone] : this.ctaColor.on(this.colors.white.raw, this.colors.black.raw)
      }
    });
    this.stylesModeOutline = () => ({
      ":host": {
        ...this.stylesModeActionAndOutline(),
        background: "transparent",
        borderColor: this.tone ? this.colors.tone[this.tone] : this.ctaColor,
        color: this.ctaColor
      }
    });
    this.stylesModeText = () => ({
      ":host": {
        background: "transparent",
        display: "inline-flex",
        "#innerwrapper": { padding: "0" },
        "#link": { padding: 0 }
      }
    });
    this.stylesModeIcon = () => ({
      ":host": {
        background: "transparent",
        borderRadius: "50%",
        "#innerwrapper": {
          height: "25px",
          width: "25px",
          justifyContent: "center",
          alignItems: "center",
          padding: "0"
        },
        "#icon": {
          fontSize: "18px"
        },
        transition: "background .3s ease-out"
      },
      ":host([active])": {
        background: this.tone ? this.colors.tone[this.tone] : this.ctaColor,
        color: this.tone ? this.colors.on.tone[this.tone] : this.ctaColor
      }
    });
  }
  get ctaColor() {
    return this.colors.accent;
  }
  styles() {
    return [
      this.constructor.layoutStyles(),
      this.visualStyles(),
      this.mode === "action" ? this.stylesModeAction() : this.mode === "outline" ? this.stylesModeOutline() : this.mode === "text" ? this.stylesModeText() : this.stylesModeIcon()
    ];
  }
  // ============= END STYLES =============
  connectedCallback() {
    super.connectedCallback();
    fromEvent(this, "keydown").pipe(takeUntil(this.disconnected)).subscribe((e) => {
      if (e.code === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        const linkElem = this.shadowRoot?.getElementById("link");
        if (linkElem) {
          linkElem.click();
        } else {
          const clickEvent = new MouseEvent("click", { bubbles: true, cancelable: true });
          this.dispatchEvent(clickEvent);
        }
      }
    });
  }
  get _isIconLink() {
    return !!(this.icon && this.icon.charAt(0) === "/");
  }
  render() {
    const hasLink = !!this.getAttribute("href");
    return xComponents(
      hasLink ? html` <x-link id="link" ${injectAttributesOf(this, ["class", "tabindex"])}> ${this.innerWrapper()} </x-link> ` : this.innerWrapper()
    );
  }
  innerWrapper() {
    return html`
      <x-flex id="innerwrapper" ${css({ position: "relative" })} crossaxis-center center>
        ${this.container()}
        ${this.loading ? html`
              <x-flex h100 w100 center crossaxis-center ${css({ position: "absolute", top: "0", right: "0" })}>
                ${this.loadingContent()}
              </x-flex>
            ` : nothing}
      </x-flex>
    `;
  }
  container() {
    return html`
      <x-flex
        horizontal
        crossaxis-center
        space="xs"
        ${css({ filter: this.loading ? "blur(6px)" : "unset", transition: "filter .3s ease-out" })}
      >
        ${this.content()}
      </x-flex>
    `;
  }
  content() {
    switch (this.mode) {
      case "icon":
        return this.iconDisplay();
      default:
        return html`
          ${this.iconDisplay()}
          <x-flex horizontal space="xxs">
            <x-text tone=${this.getTextTone()} flex>
              <slot></slot>
            </x-text>
          </x-flex>
        `;
    }
  }
  getTextTone() {
    return this.mode !== "action" ? this.tone : "";
  }
  loadingContent() {
    return html` <x-icon ${css({ animation: "2s rotate linear infinite" })}> refresh </x-icon> `;
  }
  iconDisplay() {
    if (!this.icon)
      return nothing;
    if (this._isIconLink) {
      return html`
        <x-icon id="icon" ${css({ color: this.colors.tone[this.getTextTone()] })} src=${this.icon} type="img"> </x-icon>
      `;
    }
    return html` <x-icon id="icon" ${css({ color: this.colors.tone[this.getTextTone()] })}> ${this.icon} </x-icon> `;
  }
};
XButton.layoutStyles = () => ({
  ":host": {
    display: "inline-block",
    whiteSpace: "nowrap",
    userSelect: "none",
    cursor: "pointer",
    verticalAlign: "text-bottom",
    "#link": { display: "block", color: "inherit" }
  }
});
__decorateClass([
  property({ attribute: true, reflect: true })
], XButton.prototype, "href", 2);
__decorateClass([
  property({ attribute: true, reflect: true })
], XButton.prototype, "icon", 2);
__decorateClass([
  property({ attribute: true, reflect: true })
], XButton.prototype, "mode", 2);
__decorateClass([
  property({ attribute: true, reflect: true })
], XButton.prototype, "tone", 2);
__decorateClass([
  property({ attribute: true, reflect: true, type: Boolean })
], XButton.prototype, "active", 2);
__decorateClass([
  property({ attribute: true, reflect: true, type: Boolean })
], XButton.prototype, "loading", 2);
__decorateClass([
  property({ attribute: true, reflect: true })
], XButton.prototype, "tabindex", 2);
XButton = __decorateClass([
  customElement("x-button")
], XButton);
export {
  XButton
};
